/**
 * AddServerDialog Component
 *
 * Dialog for registering a new MongoDB server.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { serverApi, CreateServerInput, ServerTestResult } from '@/services/api/serverApi';

interface AddServerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AddServerDialog: React.FC<AddServerDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreateServerInput>({
    name: '',
    host: '',
    port: 27017,
    authDatabase: 'admin',
    username: '',
    password: '',
    replicaSet: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<ServerTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof CreateServerInput, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setTestResult(null); // Clear test result when form changes
    setError(null);
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const result = await serverApi.testServerConnection(formData);
      setTestResult(result);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to test connection';
      setError(errorMessage);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Server name is required');
      return;
    }

    if (!formData.host.trim()) {
      setError('Host is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await serverApi.createServer(formData);
      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create server';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      host: '',
      port: 27017,
      authDatabase: 'admin',
      username: '',
      password: '',
      replicaSet: '',
    });
    setTestResult(null);
    setError(null);
  };

  const handleClose = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add MongoDB Server</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Server Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Server Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Production Cluster"
              required
            />
          </div>

          {/* Host and Port */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="host">Host</Label>
              <Input
                id="host"
                value={formData.host}
                onChange={(e) => handleChange('host', e.target.value)}
                placeholder="e.g., 10.10.254.2 or localhost"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="port">Port</Label>
              <Input
                id="port"
                type="number"
                value={formData.port}
                onChange={(e) => handleChange('port', parseInt(e.target.value) || 27017)}
                min={1}
                max={65535}
              />
            </div>
          </div>

          {/* Authentication (Optional) */}
          <div className="space-y-3">
            <div className="text-sm font-medium text-muted-foreground">
              Authentication (Optional)
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="authDatabase">Auth Database</Label>
                <Input
                  id="authDatabase"
                  value={formData.authDatabase}
                  onChange={(e) => handleChange('authDatabase', e.target.value)}
                  placeholder="admin"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="replicaSet">Replica Set</Label>
                <Input
                  id="replicaSet"
                  value={formData.replicaSet}
                  onChange={(e) => handleChange('replicaSet', e.target.value)}
                  placeholder="e.g., rs0"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => handleChange('username', e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div
              className={`p-3 rounded-lg text-sm ${
                testResult.success
                  ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}
            >
              {testResult.success ? (
                <>
                  <div className="font-medium">Connection successful!</div>
                  <div className="text-xs mt-1">
                    Latency: {testResult.latencyMs}ms
                    {testResult.serverInfo?.version && ` • MongoDB v${testResult.serverInfo.version}`}
                    {testResult.databases && testResult.databases.length > 0 && (
                      <span> • {testResult.databases.length} existing databases</span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-medium">Connection failed</div>
                  <div className="text-xs mt-1">{testResult.error}</div>
                </>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg text-sm bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={isTesting || !formData.host}
            >
              {isTesting ? 'Testing...' : 'Test Connection'}
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.name || !formData.host}
            >
              {isSubmitting ? 'Adding...' : 'Add Server'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddServerDialog;
